import {formatDate} from '@angular/common';
import {DEFAULT_CURRENCY} from 'src/app/config/app.config';
import {PAYMENT_STATUS} from 'src/app/core/enums/payment-status.enum';
import {IDrawer} from 'src/app/core/interfaces/drawer.interface';
import {IWaiter} from 'src/app/core/interfaces/waiter.interface';
import {PAYMENT_METHOD} from 'src/app/shared/enums/payment-method.enum';
import {PAYMENT_TYPE} from 'src/app/shared/enums/payment-type.enum';
import {roundByEpsilon} from 'src/app/shared/utils/math.utils';
import * as uuid from 'uuid';
import {Entry} from '../../../entry/domain/model/entry.model';
import {ENTRY_TYPE} from '../../../entry/domain/enum/entry-type.enum';
import {Payment} from '../../../payment/domain/model/payment.model';
import {signal} from "@angular/core";
import {ILocation} from "@core/interfaces/location_legacy.interface";

export class Account {
  constructor(
    private _id: string = uuid.v4(),
    readonly negocio_id: string = '',
    private _closed: boolean = false,
    private _waiter_id: string = '',
    private _device_id: string = '',
    private _cash_drawer_id: string = '',
    readonly _products_quantity = signal<number>(0),
    private _total: number = 0,
    readonly service_id: string = '',
    private _location_id: string = '',
    private _entries: Map<string, Entry> = new Map(),
    readonly trackId: string = uuid.v4(),
    private _payments_ids: string[] = [],
    readonly refund_ids: string[] = [],
    private _payments: Map<string, Payment> = new Map(),
    readonly zona_id: string = '',
    private _created_at: number = 0,
    readonly created_by: string = '',
    readonly updated_at: number = 0,
    readonly removed_at: number = 0,
    private _closed_at: number = 0,
    private _note: string = '',
    readonly name: string = '',
    private _formatedDate: string = '',
    private _formatedHour: string = '',
    private _closedFormatedHour: string = '',
    readonly ticket_id: string = '',
    private _serie: string = '',
    readonly ticketNumber: number = 0,
    readonly ticketSerie: string = '',
    readonly ticketPrefix: string = '',
    private _totalPaid: number = 0,
    private _totalRemaining: number = 0,
    private _priceCurrency: string = DEFAULT_CURRENCY,
    readonly color: string = '',
    private _location: ILocation | null = null,
    private _drawer: IDrawer | null = null,
    private _waiter: IWaiter | null = null,
    private _serverUpdatedAt: number = Date.now(),
    private _actualUpdatedAt: number = Date.now(),
    private _booking_id: string = '',
    private _isLocked = false,
  ) {
    this.initializePayments();

    if (this._created_at) {
      this.initDates(this._created_at);
    }

    if (this._closed_at) {
      this.setCloseTime(this._closed_at);
    }

    this.calculateAccountInfo();
  }

  private _paymentIdsSet = new Set<string>(this._payments_ids);
  private _paymentsMapByMethod: Map<PAYMENT_METHOD, Payment> = new Map();
  private _paymentsMapByType: Map<PAYMENT_TYPE, Payment> = new Map();
  private _paymentsMapByStatus: Map<PAYMENT_STATUS, Payment> = new Map();

  private _isTotalAmountDirty: boolean = true;
  private _isTotalPaidDirty: boolean = true;
  private _isTotalRemainingDirty: boolean = true;
  private _isTotalProductsDirty: boolean = true;

  get isLocked(): boolean {
    return this._isLocked;
  }

  get id(): string {
    return this._id;
  }

  get device_id(): string {
    return this._device_id;
  }

  get closed(): boolean {
    return this._closed;
  }

  get bookingId(): string {
    return this._booking_id;
  }

  get waiterId(): string {
    return this._waiter_id;
  }

  get cashDrawerId(): string {
    return this._cash_drawer_id;
  }

  get drawer(): IDrawer | null {
    return this._drawer;
  }

  get waiter(): IWaiter | null {
    return this._waiter;
  }

  get location(): ILocation | null {
    return this._location;
  }

  get locationId(): string {
    return this._location_id;
  }

  get payments(): Payment[] {
    return Array.from(this._payments.values());
  }

  get total(): number {
    return this._total;
  }

  get entries(): Entry[] {
    return Array.from(this._entries.values());
  }

  get hasEntries(): boolean {
    return !!this.entries?.length;
  }

  get hasPayments(): boolean {
    return !!this.payments?.length;
  }

  get hasProductPayments(): boolean {
    return (
      !!this.payments?.length &&
      this.payments.some(
        (payment: Payment) => payment.payment_type === PAYMENT_TYPE.byProduct
      )
    );
  }

  get hasPaymentsWithoutProducts(): boolean {
    return (
      this.payments?.some((payment: Payment) => !payment.entries?.length) ??
      false
    );
  }

  get entriesTotal(): number {
    return this.entries
      .filter((entry) => entry.type !== ENTRY_TYPE.booking)
      .reduce(
        (accumulator, entry) => roundByEpsilon(accumulator + entry.total),
        0
      );
  }

  get paymentsTotal(): number {
    return this.payments
      .filter((payment) => payment.status === PAYMENT_STATUS.completed)
      .reduce(
        (accumulator, payment) => roundByEpsilon(accumulator + payment.amount),
        0
      );
  }

  get paymentEntries(): Entry[] {
    return this.payments
      .filter((payment) => payment.status === PAYMENT_STATUS.completed)
      .flatMap((payment) => payment.entries);
  }

  get paymentIds(): string[] {
    return this._payments_ids ?? [];
  }

  get totalPaid(): number {
    return this._totalPaid ?? 0;
  }

  get totalRemaining(): number {
    return this._totalRemaining ?? 0;
  }

  get productsQuantity(): number {
    return this._products_quantity();
  }

  get closedAt(): number {
    return this._closed_at ?? 0;
  }

  get createdAt(): number {
    return this._created_at ?? 0;
  }

  get serverUpdatedAt(): number {
    return this._serverUpdatedAt ?? 0;
  }

  get actualUpdatedAt(): number {
    return this._actualUpdatedAt ?? 0;
  }

  get formatedDate(): string {
    return this._formatedDate ?? '';
  }

  get formatedHour(): string {
    return this._formatedHour ?? '';
  }

  get closedFormatedHour(): string {
    return this._closedFormatedHour ?? '';
  }

  get serie(): string {
    return this._serie;
  }

  get note(): string {
    return this._note;
  }

  get priceCurrency(): string {
    return this._priceCurrency;
  }

  updatePriceCurrency(priceCurrency: string): void {
    this._priceCurrency = priceCurrency;
  }

  updateDrawer(drawer: IDrawer | null): void {
    this._drawer = drawer;
  }

  updateWaiter(waiter: IWaiter | null): void {
    this._waiter = waiter;
  }

  updateWaiterId(waiterId: string): void {
    this._waiter_id = waiterId;
  }

  updateNote(note: string): void {
    this._note = note;
  }

  updateCreatedAccountTime(time: Date): void {
    this._created_at = time.getTime();
    this.initDates(this._created_at);
  }

  updateClosedAccountTime(time: Date): void {
    this._closed_at = time.getTime();
    this.setCloseTime(this._closed_at);
  }

  updateBookingId(bookingId: string): void {
    this._booking_id = bookingId;
  }

  updateLocation(location: ILocation | null): void {
    this._location = location;
  }

  updateCashDrawerId(cashDrawerId: string): void {
    this._cash_drawer_id = cashDrawerId;
  }

  updateLocationId(locationId: string): void {
    this._location_id = locationId;
  }

  updateClosed(closed: boolean): void {
    this._closed = closed;
  }

  updateLock(isLocked: boolean): void {
    this._isLocked = isLocked;
  }

  updateDeviceId(id: string): void {
    this._device_id = id;
  }

  markAsSent(): void {
    const now = Date.now();

    this._actualUpdatedAt = now;
    this._serverUpdatedAt = now;
  }

  addPayment(payment: Payment): void {
    if (!payment || !payment._id) {
      throw new Error(`Invalid payment on account ${this._id}`);
    }

    this._payments.set(payment._id, payment);

    this.updatePaymentsIds(payment._id);

    if (payment.payment_method) {
      this._paymentsMapByMethod.set(payment.payment_method, payment);
    }

    if (payment.payment_type) {
      this._paymentsMapByType.set(payment.payment_type, payment);
    }

    if (payment.status) {
      this._paymentsMapByStatus.set(payment.status, payment);
    }

    this._isTotalAmountDirty = true;
    this._isTotalPaidDirty = true;
    this._isTotalRemainingDirty = true;

    this.updateActualUpdatedAt();

    this.calculateAccountInfo();
  }

  updatePayment(payment: Payment): void {
    if (!payment || !payment._id) {
      throw new Error(`Invalid payment on account ${this._id}`);
    }

    if (!this._payments.has(payment._id)) {
      throw new Error(
        `Payment with id ${payment._id} not found on account ${this._id}`
      );
    }

    const oldPayment = this._payments.get(payment._id);

    if (oldPayment?.payment_method) {
      this._paymentsMapByMethod.delete(oldPayment.payment_method);
    }

    if (oldPayment?.payment_type) {
      this._paymentsMapByType.delete(oldPayment.payment_type);
    }

    if (oldPayment?.status) {
      this._paymentsMapByStatus.delete(oldPayment.status);
    }

    this._payments.set(payment._id, payment);
    this._paymentIdsSet.add(payment._id);

    if (payment.payment_method) {
      this._paymentsMapByMethod.set(payment.payment_method, payment);
    }

    if (payment.payment_type) {
      this._paymentsMapByType.set(payment.payment_type, payment);
    }

    if (payment.status) {
      this._paymentsMapByStatus.set(payment.status, payment);
    }

    this._isTotalAmountDirty = true;
    this._isTotalPaidDirty = true;
    this._isTotalRemainingDirty = true;

    this.updateActualUpdatedAt();

    this.calculateAccountInfo();
  }

  deletePayment(paymentId: string): void {
    if (!paymentId) {
      throw new Error(`No payment ID provided for account ${this._id}`);
    }

    if (!this._payments.has(paymentId)) {
      throw new Error(
        `Payment with id ${paymentId} not found on account ${this._id}`
      );
    }

    const oldPayment = this._payments.get(paymentId);

    this._payments.delete(paymentId);

    this._paymentIdsSet.delete(oldPayment?._id ?? '');

    if (oldPayment?.payment_method) {
      this._paymentsMapByMethod.delete(oldPayment.payment_method);
    }

    if (oldPayment?.payment_type) {
      this._paymentsMapByType.delete(oldPayment.payment_type);
    }

    if (oldPayment?.status) {
      this._paymentsMapByStatus.delete(oldPayment.status);
    }

    this._isTotalAmountDirty = true;
    this._isTotalPaidDirty = true;
    this._isTotalRemainingDirty = true;

    this.updateActualUpdatedAt();

    this.calculateAccountInfo();
  }

  updatePayments(payments: Payment[]): void {
    this._payments = new Map(payments.map((payment) => [payment._id, payment]));
  }

  getPaymentByMethod(paymentMethod: PAYMENT_METHOD): Payment | undefined {
    return this._paymentsMapByMethod.get(paymentMethod);
  }

  getPaymentByType(paymentType: PAYMENT_TYPE): Payment | undefined {
    return this._paymentsMapByType.get(paymentType);
  }

  getPaymentByStatus(paymentStatus: PAYMENT_STATUS): Payment | undefined {
    return this._paymentsMapByStatus.get(paymentStatus);
  }

  getPaymentsByStatus(paymentStatus: PAYMENT_STATUS): Payment[] {
    return this.payments.filter(
      (payment: Payment) => payment.status === paymentStatus
    );
  }

  addEntry(entry: Entry): void {
    if (!entry || !entry._id) {
      throw new Error(`Invalid entry on account ${this._id}`);
    }

    this._entries.set(entry._id, entry);

    this._isTotalAmountDirty = true;
    this._isTotalProductsDirty = true;
    this._isTotalRemainingDirty = true;

    this.updateActualUpdatedAt();

    this.calculateAccountInfo();
  }

  updateEntry(entryToUpdate: Entry): void {
    if (!entryToUpdate || !entryToUpdate._id) {
      return;
    }

    if (this._entries.has(entryToUpdate._id)) {
      this._entries.set(entryToUpdate._id, entryToUpdate);

      this._isTotalAmountDirty = true;
      this._isTotalProductsDirty = true;
      this._isTotalRemainingDirty = true;

      this.updateActualUpdatedAt();

      this.calculateAccountInfo();
    }
  }

  updateEntries(entriesToUpdate: Entry[]): void {
    if (!entriesToUpdate || entriesToUpdate.length === 0) {
      return;
    }

    entriesToUpdate.forEach((entryToUpdate) => {
      if (entryToUpdate && entryToUpdate._id) {
        this._entries.set(entryToUpdate._id, entryToUpdate);

        this._isTotalAmountDirty = true;
        this._isTotalProductsDirty = true;
        this._isTotalRemainingDirty = true;

        this.updateActualUpdatedAt();
      }
    });

    this.calculateAccountInfo();
  }

  removeEntry(entryToRemove: Entry): void {
    if (!entryToRemove || !entryToRemove._id) {
      return;
    }

    if (this._entries.delete(entryToRemove._id)) {

      this._isTotalAmountDirty = true;
      this._isTotalProductsDirty = true;
      this._isTotalRemainingDirty = true;

      this.updateActualUpdatedAt();

      this.calculateAccountInfo();
    }
  }

  removeAllEntries(): void {
    if (this.entries.length > 0) {
      this._entries.clear();

      this._isTotalAmountDirty = true;
      this._isTotalProductsDirty = true;
      this._isTotalRemainingDirty = true;

      this.updateActualUpdatedAt();

      this.calculateAccountInfo();
    }
  }

  hasEntry(entryId: string): boolean {
    return this._entries.has(entryId);
  }

  getEntryByType(entryType: ENTRY_TYPE): Entry | undefined {
    return Array.from(this._entries.values()).find(entry => entry.type === entryType);
  }

  updateBookingEntry(newPrice: number): Entry | undefined {
    const bookingEntry = this.getEntryByType(ENTRY_TYPE.booking);

    if (!bookingEntry) {
      return undefined;
    }

    if (newPrice > 0) {
      bookingEntry.updateUnitPriceModified(newPrice);
      bookingEntry.updateInitialUnitPrice(newPrice);
      bookingEntry.changeEntryQuantity(1);
      bookingEntry.updateQuantityToDisplay(1);
      bookingEntry.recalculateTotal();
    } else {
      bookingEntry.updateUnitPriceModified(0);
      bookingEntry.updateInitialUnitPrice(0);
      bookingEntry.changeEntryQuantity(0);
      bookingEntry.updateQuantityToDisplay(0);
      bookingEntry.recalculateTotal();
    }

    return bookingEntry;
  }

  private updatePaymentsIds(paymentId: string): void {
    this._paymentIdsSet.add(paymentId);
    this._payments_ids = Array.from(this._paymentIdsSet);
  }

  private initializePayments(): void {
    this._paymentIdsSet = new Set<string>(this.paymentIds || []);

    this._paymentsMapByMethod = new Map();
    this._paymentsMapByType = new Map();
    this._paymentsMapByStatus = new Map();

    if (this.payments && this.payments.length) {
      this.payments.forEach((payment) => {
        if (!payment || !payment._id) {
          return;
        }

        this._paymentIdsSet.add(payment._id);

        if (payment.payment_method) {
          this._paymentsMapByMethod.set(payment.payment_method, payment);
        }

        if (payment.payment_type) {
          this._paymentsMapByType.set(payment.payment_type, payment);
        }

        if (payment.status) {
          this._paymentsMapByStatus.set(payment.status, payment);
        }
      });
    }
  }

  private updateActualUpdatedAt(): void {
    this._actualUpdatedAt = Date.now();
  }

  private calculateAccountInfo(): void {
    if (this._isTotalAmountDirty) {
      this.calculateTotalAmount();
      this._isTotalAmountDirty = false;
    }

    if (this._isTotalPaidDirty) {
      this.calculateTotalPaid();
      this._isTotalPaidDirty = false;
    }

    if (this._isTotalRemainingDirty) {
      this.calculateTotalRemaining();
      this._isTotalRemainingDirty = false;
    }

    if (this._isTotalProductsDirty) {
      this.calculateTotalProducts();
      this._isTotalProductsDirty = false;
    }
  }

  private calculateTotalAmount(): number {
    let amount = 0;

    for (const entry of this.entries) {
      amount += roundByEpsilon(entry.total);
    }

    const newTotal = roundByEpsilon(amount);

    if (newTotal !== this.total && this.total !== undefined) {
      this.updateActualUpdatedAt();
    }

    this._total = newTotal;

    return this._total;
  }

  private calculateTotalPaid(): number {
    let totalPaid = 0;

    for (const payment of this.payments) {
      if (
        (!payment.entries || payment.entries.length === 0) &&
        payment.status === PAYMENT_STATUS.completed
      ) {
        totalPaid += payment.amount;
      }
    }

    const newTotalPaid = roundByEpsilon(totalPaid);

    if (newTotalPaid !== this.totalPaid && this.totalPaid !== 0) {
      this.updateActualUpdatedAt();
    }

    this._totalPaid = newTotalPaid;

    return this.totalPaid;
  }

  private calculateTotalRemaining(): number {
    if (this.total !== undefined) {
      const newTotalRemaining = roundByEpsilon(this.total - this.totalPaid);

      if (
        newTotalRemaining !== this.totalRemaining &&
        this.totalRemaining != 0
      ) {
        this.updateActualUpdatedAt();
      }

      this._totalRemaining = newTotalRemaining;
    } else {
      this._totalRemaining = 0;
    }

    return this.totalRemaining;
  }

  private calculateTotalProducts(): number {
    if (this._isTotalProductsDirty) {
      let totalProducts = 0;

      for (const entry of this.entries) {
        if (entry.type !== ENTRY_TYPE.booking) {
          totalProducts += entry.quantityToDisplay;
        }
      }

      const newTotalProducts = totalProducts;

      if (newTotalProducts !== this.productsQuantity) {
        this.updateActualUpdatedAt();
      }

      this._products_quantity.set(newTotalProducts);
      this._isTotalProductsDirty = false;
    }

    return this.productsQuantity;
  }

  private initDates(createdAt: number): void {
    const date = new Date(createdAt);

    this._formatedDate = formatDate(date, 'd MMM. HH:mm', 'es-ES');
    this._formatedHour = formatDate(date, 'HH:mm', 'es-ES');
  }

  /**
   * Sets the hour time where the account has been closed
   *
   * @private
   * @param {(number | undefined)} time
   * @return {*}
   * @memberof Account
   */
  setCloseTime(time: number): void {
    if (time) {
      const date = new Date(time);

      this._closedFormatedHour = formatDate(date, 'HH:mm', 'es-ES');
    } else {
      this._closedFormatedHour = '';
    }
  }

  updateSerie(serie: string): void {
    this._serie = serie;
  }
}
